import React from 'react';

import { Flex, Group, Skeleton, Text } from '@mantine/core';
import type { PlotlyTypes } from 'visyn_core/plotly';
import { PlotlyComponent } from 'visyn_core/plotly';
import { categoricalColors20 } from 'visyn_core/utils';

import CountPercentageToggle from './CountPercentageToggle';
import type { IgChainRawData } from '../generated/generatedOpenAPI';
import { DisplayType } from '../types/experiment';
import { toPercentages } from '../utils';

interface IgChainSummaryProps {
  igChainRawData: IgChainRawData | undefined;
}

export default function IgChainSummary({ igChainRawData }: IgChainSummaryProps) {
  const [selectedDisplayType, setSelectedDisplayType] = React.useState<DisplayType>(DisplayType.COUNT);

  const data = React.useMemo(() => {
    if (!igChainRawData) {
      return [];
    }
    let vals: number[] = Object.values(igChainRawData);
    if (selectedDisplayType === DisplayType.PERCENTAGE) {
      vals = toPercentages(Object.values(igChainRawData));
    }

    return [
      {
        type: 'bar',
        x: ['IGH+IGK', 'IGH+IGL', 'IGH', 'IGK', 'IGL'],
        y: vals,
        marker: {
          color: categoricalColors20[0],
        },
      },
    ];
  }, [igChainRawData, selectedDisplayType]);

  if (!igChainRawData) {
    return <Skeleton height={300} />;
  }

  const yAxisLabel: string = selectedDisplayType === DisplayType.PERCENTAGE ? 'Percentage of cells' : 'Cells';

  return (
    <Flex h="100%" justify="flex-end" direction="column" data-testid="ig-chain-summary">
      <Group justify="space-between">
        <Text>Chain Pairing Distribution</Text>
        <Group gap={7}>
          <CountPercentageToggle displayType={selectedDisplayType} setDisplayType={setSelectedDisplayType} />
        </Group>
      </Group>
      <PlotlyComponent
        data={data as PlotlyTypes.Data[]}
        style={{ width: '100%' }}
        layout={{
          height: 400,
          title: {
            font: {
              size: 18,
            },
          },
          margin: {
            t: 30,
            b: 55,
          },
          yaxis: {
            title: {
              text: yAxisLabel,
              font: { size: 16 },
            },
          },
        }}
        config={{
          displaylogo: false,
          modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'autoScale2d', 'select2d'],
        }}
      />
    </Flex>
  );
}
